import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {MachineTag} from '@models/adminstration/machine/tag/machine-tag';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {MachineTagService} from '@services/administration/machine/tag/machine-tag.service';

@Component({
  selector: 'scitis-machine-tag',
  templateUrl: './machine-tag.component.html',
  styleUrls: ['./machine-tag.component.scss'],
  standalone: false
})
export class MachineTagComponent implements OnChanges, OnInit {
  @Input()
  category: string = '';

  @Input()
  isAddOperation: boolean = false;

  @Input()
  machineTag: string = '';

  @Input()
  isTemplate: boolean = false;

  @Input()
  isTechAdminOrHigher: boolean = false;

  @Input()
  isAdminOrHigher: boolean = false;

  @Output()
  machineTagChange: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  isTemplateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public isLoadingMachineTags: boolean = false;
  public isNewTagAdded: boolean = false;

  public possibleMachineTags: MachineTag[] = [];
  public addMachineTagText: string = '';
  public templateFlagInitialValue: boolean = false;
  public machineBecomesTemplateMessage: string = '';
  public machineIsSetAsTemplateMachine: string = '';

  private COMPONENT_NAME: string = 'MachineTagComponent';

  constructor(private machineTagService: MachineTagService,
              private translateService: TranslateService,
              private notificationService: NotificationService) {
    this.addMachineTagText = this.translateService.instant('Add Machine tag');

  }

  public get shouldDisableTemplateSlider() {
    return !this.isTechAdminOrHigher || this.templateFlagInitialValue || this.isNewTagAdded || this.isAddOperation || !this.machineTag || this.machineTag.length === 0 || !this.isAdminOrHigher;
  }

  public get shouldShowReadOnlyControlsForTemplateMachine() {
    return this.templateFlagInitialValue && !this.isAddOperation;
  }

  public get shouldShowMachineBecomingTemplateWarning() {
    return !this.isNewTagAdded && !this.templateFlagInitialValue && this.isTemplate && !this.isAddOperation && this.machineTag && !this.machineTag.isNullOrWhitespace();
  }

  ngOnInit(): void {
    this.templateFlagInitialValue = this.isTemplate;
    this.machineBecomesTemplateMessage = this.translateService.instant('This machine will become the new template machine for %tag tag and  %category category.')
    .replaceAll('%tag', this.machineTag ? this.machineTag.toBold() : '').replace('%category', this.category.toBold());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.category) {
      this.getPossibleMachineTags(changes.category.currentValue);
    }
    this.machineIsSetAsTemplateMachine = this.translateService.instant('This machine will become the new template machine for %tag tag and  %category category.')
    .replaceAll('%tag', this.machineTag ? this.machineTag.toBold() : '').replace('%category', this.category.toBold());
  }

  onSelectTag(machineTag: MachineTag | undefined) {
    if (!machineTag || !machineTag.tag) {
      this.machineTagChange.emit('');
      this.machineTag = '';
      this.isNewTagAdded = false;
      this.isTemplate = false;
      this.isTemplateChange.emit(false);
    } else {
      this.isNewTagAdded = machineTag.isNewlyAddedTag;
      this.machineTagChange.emit(machineTag.tag);
      this.isTemplateChange.emit(machineTag.isNewlyAddedTag);
    }
  }

  onAddTagEvent = (newTag: string) => {
    this.possibleMachineTags = this.machineTagService.enhanceMachineTagsArray(this.category, newTag, this.possibleMachineTags);
    this.isTemplate = true;
    this.isTemplateChange.emit(true);
    this.isNewTagAdded = true;
    const foundTag = this.possibleMachineTags.find(t => t.tag.toLowerCase() === newTag.toLowerCase());
    if (foundTag) {
      this.machineTag = foundTag.tag;
    }
    return foundTag;
  };

  onIsTemplateChange() {
    this.isTemplateChange.emit(this.isTemplate);
  }

  private getPossibleMachineTags(category: string) {
    this.isLoadingMachineTags = true;
    if (!category || category.length === 0) {
      this.possibleMachineTags = [];
      this.isLoadingMachineTags = false;
      return;
    }

    this.machineTagService.listMachineTagsForCategory(category).subscribe(
        {
          next: (machineTags: MachineTag[]) => {
            this.possibleMachineTags = this.machineTagService.enhanceMachineTagsArray(category, this.machineTag, machineTags);
            this.isLoadingMachineTags = false;
          }, error: error => {
            const message = this.translateService.instant('Error getting possible machine tags for ') + category;
            this.notificationService.error(message, error, this.COMPONENT_NAME, true);
            this.possibleMachineTags = this.machineTagService.enhanceMachineTagsArray(category, this.machineTag, []);
            this.isLoadingMachineTags = false;
          }
        }
    );
  }
}
